import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { courseService } from "../services/courseService";
import { moduleService } from "../services/moduleService";
import { lessonService } from "../services/lessonService";
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  ChevronRight,
  Loader2,
  BookOpen,
  Layers,
  GripVertical,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface Lesson {
  _id: string;
  title: string;
  order: number;
  duration: number;
  isFree: boolean;
  isPublished: boolean;
}

interface Module {
  _id: string;
  title: string;
  order: number;
  isPublished?: boolean;
  lessons: Lesson[];
  duration?: number;
}

interface Course {
  _id: string;
  title: string;
  totalModules: number;
  totalLessons: number;
}

// Sortable Lesson Component
function SortableLesson({
  lesson,
  index,
  moduleId,
  courseId,
  navigate,
  handleDeleteLesson,
  deletingLessonId,
  isCrossModuleDragOver = false,
}: {
  lesson: Lesson;
  index: number;
  moduleId: string;
  courseId: string;
  navigate: ReturnType<typeof useNavigate>;
  handleDeleteLesson: (id: string) => void;
  deletingLessonId: string | null;
  isCrossModuleDragOver?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: lesson._id,
    data: {
      type: "lesson",
      lesson,
      moduleId,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return "0m";
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h ${remainingMins}m`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-white border rounded-lg transition-colors ${
        isCrossModuleDragOver
          ? "border-[#e07828] ring-2 ring-[#e07828]/30"
          : "border-gray-100 hover:border-gray-200"
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Drag Handle for Lesson */}
        <div {...attributes} {...listeners} className="cursor-grab touch-none">
          <GripVertical
            size={16}
            className="text-gray-300 hover:text-gray-500"
          />
        </div>

        <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
          {index + 1}
        </span>
        <div>
          <p className="font-medium text-[#102040] text-sm">{lesson.title}</p>
          <p className="text-xs text-gray-500">
            {formatDuration(lesson.duration || 0)}
            {lesson.isFree && <span className="ml-2 text-green-600">Free</span>}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {lesson.isPublished ? (
          <CheckCircle2 size={14} className="text-green-500" />
        ) : (
          <XCircle size={14} className="text-yellow-500" />
        )}
        <button
          onClick={() =>
            navigate(
              `/courses/${courseId}/modules/${moduleId}/lessons/${lesson._id}`
            )
          }
          className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-blue-600"
          title="Edit Lesson"
        >
          <Edit2 size={14} />
        </button>
        <button
          onClick={() => handleDeleteLesson(lesson._id)}
          disabled={deletingLessonId === lesson._id}
          className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-red-600 disabled:opacity-50"
          title="Delete Lesson"
        >
          {deletingLessonId === lesson._id ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Trash2 size={14} />
          )}
        </button>
      </div>
    </div>
  );
}

// Sortable Module Component
function SortableModule({
  module,
  index,
  courseId,
  expandedModules,
  toggleModuleExpand,
  handleDeleteModule,
  handleDeleteLesson,
  navigate,
  deletingModuleId,
  deletingLessonId,
  activeDrag,
  overInfo,
}: {
  module: Module;
  index: number;
  courseId: string;
  expandedModules: Set<string>;
  toggleModuleExpand: (id: string) => void;
  handleDeleteModule: (id: string) => void;
  handleDeleteLesson: (id: string) => void;
  navigate: ReturnType<typeof useNavigate>;
  deletingModuleId: string | null;
  deletingLessonId: string | null;
  activeDrag: null | { id: string; type: "module" | "lesson"; moduleId?: string };
  overInfo: null | { id: string; type: "module" | "lesson"; moduleId?: string };
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: module._id,
    data: {
      type: "module",
      module,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return "0m";
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h ${remainingMins}m`;
  };

  return (
    <Card ref={setNodeRef} style={style} className="overflow-hidden mb-4">
      {/* Module Header */}
      <div
        className="p-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => toggleModuleExpand(module._id)}
      >
        <div className="flex items-center gap-3">
          <div
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="cursor-grab touch-none"
          >
            <GripVertical size={18} className="text-gray-400" />
          </div>
          <div className="w-8 h-8 rounded-full bg-[#e07828] flex items-center justify-center text-white font-bold text-sm">
            {index + 1}
          </div>
          <div>
            <h3 className="font-semibold text-[#102040]">{module.title}</h3>
            <p className="text-xs text-gray-500">
              {module.lessons?.length || 0} lessons •{" "}
              {formatDuration(module.duration || 0)}
            </p>
          </div>
          {module.isPublished ? (
            <span className="ml-3 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
              <CheckCircle2 size={10} /> Published
            </span>
          ) : (
            <span className="ml-3 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full flex items-center gap-1">
              <XCircle size={10} /> Draft
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/courses/${courseId}/modules/${module._id}/edit`);
            }}
            className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-blue-600 transition-colors"
            title="Edit Module"
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteModule(module._id);
            }}
            disabled={deletingModuleId === module._id}
            className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50"
            title="Delete Module"
          >
            {deletingModuleId === module._id ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Trash2 size={16} />
            )}
          </button>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              expandedModules.has(module._id) ? "rotate-90" : ""
            }`}
          />
        </div>
      </div>

      {/* Lessons List (Expanded) */}
      {expandedModules.has(module._id) && (
        <div className="p-4">
          {module.lessons && module.lessons.length > 0 ? (
            <SortableContext
              items={module.lessons.map((l) => l._id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {module.lessons.map((lesson, lessonIndex) => (
                  <SortableLesson
                    key={lesson._id}
                    lesson={lesson}
                    index={lessonIndex}
                    moduleId={module._id}
                    courseId={courseId}
                    navigate={navigate}
                    handleDeleteLesson={handleDeleteLesson}
                    deletingLessonId={deletingLessonId}
                    isCrossModuleDragOver={
                      activeDrag?.type === "lesson" &&
                      activeDrag.moduleId !== module._id &&
                      overInfo?.id === lesson._id
                    }
                  />
                ))}
              </div>
            </SortableContext>
          ) : (
            <p className="text-gray-500 text-sm text-center py-4">
              No lessons in this module yet.
            </p>
          )}

          {/* Add Lesson Button */}
          <button
            onClick={() =>
              navigate(`/courses/${courseId}/modules/${module._id}/lessons/add`)
            }
            className="mt-3 w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-[#e07828] hover:text-[#e07828] transition-colors"
          >
            <Plus size={18} />
            Add Lesson
          </button>
        </div>
      )}
    </Card>
  );
}

export default function CourseStructurePage() {
  const navigate = useNavigate();
  const { id: courseId } = useParams<{ id: string }>();

  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    new Set()
  );
  const [deletingModuleId, setDeletingModuleId] = useState<string | null>(null);
  const [deletingLessonId, setDeletingLessonId] = useState<string | null>(null);
  const [activeDrag, setActiveDrag] = useState<
    null | { id: string; type: "module" | "lesson"; moduleId?: string }
  >(null);
  const [overInfo, setOverInfo] = useState<
    null | { id: string; type: "module" | "lesson"; moduleId?: string }
  >(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchData = useCallback(async () => {
    if (!courseId) return;
    setIsLoading(true);
    setError(null);
    try {
      const [courseRes, modulesRes] = await Promise.all([
        courseService.getCourseById(courseId),
        moduleService.getModulesByCourse(courseId),
      ]);
      setCourse(courseRes.course);

      const fetchedModules = (modulesRes.modules as unknown as Module[]) || [];
      // Sort modules by order
      fetchedModules.sort((a, b) => a.order - b.order);
      // Sort lessons within modules by order
      fetchedModules.forEach((m) => {
        if (m.lessons) {
          m.lessons.sort((a, b) => a.order - b.order);
        }
      });

      setModules(fetchedModules);
      // Expand all modules by default
      setExpandedModules(new Set(fetchedModules.map((m: Module) => m._id)));
    } catch (err: any) {
      console.error("Failed to fetch data:", err);
      setError("Failed to load course structure.");
    } finally {
      setIsLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleModuleExpand = (moduleId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  const handleDeleteModule = async (moduleId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this module and all its lessons?"
      )
    ) {
      return;
    }
    setDeletingModuleId(moduleId);
    try {
      await moduleService.deleteModule(moduleId);
      fetchData();
    } catch (err) {
      console.error("Failed to delete module:", err);
    } finally {
      setDeletingModuleId(null);
    }
  };

  const handleDeleteLesson = async (lessonId: string) => {
    if (!confirm("Are you sure you want to delete this lesson?")) {
      return;
    }
    setDeletingLessonId(lessonId);
    try {
      await lessonService.deleteLesson(lessonId);
      fetchData();
    } catch (err) {
      console.error("Failed to delete lesson:", err);
    } finally {
      setDeletingLessonId(null);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current;
    if (data?.type === "module") {
      setActiveDrag({ id: String(event.active.id), type: "module" });
      setOverInfo(null);
    } else if (data?.type === "lesson") {
      setActiveDrag({
        id: String(event.active.id),
        type: "lesson",
        moduleId: data.moduleId,
      });
      setOverInfo(null);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const data = event.over?.data.current;
    if (!data) {
      setOverInfo(null);
      return;
    }
    if (data?.type === "module") {
      setOverInfo({ id: String(event.over!.id), type: "module" });
    } else if (data?.type === "lesson") {
      setOverInfo({
        id: String(event.over!.id),
        type: "lesson",
        moduleId: data.moduleId,
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    // Always clear drag tracking on drag end, before any early returns.
    setActiveDrag(null);
    setOverInfo(null);

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const type = active.data.current?.type;

    if (type === "module") {
      const oldIndex = modules.findIndex((m) => m._id === active.id);
      const newIndex = modules.findIndex((m) => m._id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newModules = arrayMove(modules, oldIndex, newIndex);

        // Updates orders
        const updatedModules = newModules.map((m, idx) => ({
          ...m,
          order: idx + 1,
        }));

        setModules(updatedModules);

        // Persist changes
        try {
          const updatePromises = updatedModules.map((m) =>
            moduleService.updateModule(m._id, { order: m.order })
          );
          await Promise.all(updatePromises);
        } catch (err) {
          console.error("Failed to update module order:", err);
        }
      }
    } else if (type === "lesson") {
      const sourceModuleId = active.data.current?.moduleId as string | undefined;
      const overType = over.data.current?.type;

      // Drop on a module header or empty area is not in scope (chosen UX is
      // drop-on-lesson-row). Ignore silently.
      if (overType !== "lesson") {
        return;
      }

      const targetModuleId = over.data.current?.moduleId as string | undefined;
      if (!sourceModuleId || !targetModuleId) return;

      // ---- Same-module reorder (existing behavior) ----
      if (sourceModuleId === targetModuleId) {
        const moduleIndex = modules.findIndex((m) => m._id === sourceModuleId);
        if (moduleIndex === -1) return;
        const currentModule = modules[moduleIndex];
        if (!currentModule) return;

        const oldIndex = currentModule.lessons.findIndex(
          (l) => l._id === active.id,
        );
        const newIndex = currentModule.lessons.findIndex(
          (l) => l._id === over.id,
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          const newLessons = arrayMove(currentModule.lessons, oldIndex, newIndex);
          const updatedLessons = newLessons.map((l, idx) => ({
            ...l,
            order: idx + 1,
          }));
          const newModule: Module = { ...currentModule, lessons: updatedLessons };
          const newModules = [...modules];
          newModules[moduleIndex] = newModule;
          setModules(newModules);

          try {
            const updatePromises = updatedLessons.map((l) =>
              lessonService.updateLesson(l._id, { order: l.order }),
            );
            await Promise.all(updatePromises);
          } catch (err) {
            console.error("Failed to update lesson order:", err);
          }
        }
      } else {
        // ---- Cross-module move (NEW BRANCH) ----
        const sourceModuleIndex = modules.findIndex(
          (m) => m._id === sourceModuleId,
        );
        const targetModuleIndex = modules.findIndex(
          (m) => m._id === targetModuleId,
        );
        if (sourceModuleIndex === -1 || targetModuleIndex === -1) return;

        const sourceModule = modules[sourceModuleIndex];
        const targetModule = modules[targetModuleIndex];
        if (!sourceModule || !targetModule) return;

        const movedLesson = sourceModule.lessons.find(
          (l) => l._id === active.id,
        );
        if (!movedLesson) return;

        // Compute the insertion index in the target module from over.id
        const targetLessonIds = targetModule.lessons.map((l) => String(l._id));
        const overIndexInTarget = targetLessonIds.indexOf(String(over.id));
        const insertIndex =
          overIndexInTarget === -1 ? targetLessonIds.length : overIndexInTarget;
        const newOrder = insertIndex + 1; // 1-based

        // ---- Optimistic state update ----
        const newSourceLessons = sourceModule.lessons
          .filter((l) => l._id !== active.id)
          .map((l, idx) => ({ ...l, order: idx + 1 }));
        const newTargetLessons = [
          ...targetModule.lessons.slice(0, insertIndex),
          {
            ...movedLesson,
            order: newOrder,
            module: targetModuleId,
          } as Lesson,
          ...targetModule.lessons.slice(insertIndex),
        ].map((l, idx) => ({ ...l, order: idx + 1 }));

        const optimisticModules = modules.map((m, idx) => {
          if (idx === sourceModuleIndex) return { ...m, lessons: newSourceLessons };
          if (idx === targetModuleIndex) return { ...m, lessons: newTargetLessons };
          return m;
        });
        setModules(optimisticModules);

        // ---- Persist via atomic backend endpoint ----
        try {
          await lessonService.moveLesson(String(active.id), {
            targetModuleId,
            newOrder,
          });
        } catch (err) {
          console.error("Failed to move lesson between modules:", err);
          await fetchData(); // rollback by re-fetching server state
        }
      }
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#e07828]" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !course) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <p className="text-red-500 text-lg">{error || "Course not found"}</p>
          <Button
            onClick={() => navigate("/courses")}
            variant="outline"
            className="mt-4"
          >
            Back to Courses
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/courses"
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[#102040]">
                Course Structure
              </h1>
              <p className="text-[#706F6F] text-sm">{course.title}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2">
            <Link
              to={`/courses/edit/${courseId}`}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm w-full sm:w-auto"
            >
              <Edit2 size={16} />
              Edit Course
            </Link>
            <Button
              variant="primary"
              onClick={() => navigate(`/courses/${courseId}/modules/add`)}
              className="w-full sm:w-auto"
            >
              <Plus size={18} className="mr-1" />
              Add Module
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <Card className="p-4 sm:p-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Layers size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#102040]">
                {modules.length}
              </p>
              <p className="text-sm text-gray-500">Modules</p>
            </div>
          </Card>
          <Card className="p-4 sm:p-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <BookOpen size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#102040]">
                {modules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0)}
              </p>
              <p className="text-sm text-gray-500">Total Lessons</p>
            </div>
          </Card>
        </div>

        {/* Modules List */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={modules.map((m) => m._id)}
            strategy={verticalListSortingStrategy}
          >
            {modules.length === 0 ? (
              <Card className="p-8 text-center">
                <Layers size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  No modules yet
                </h3>
                <p className="text-gray-500 mb-4">
                  Start building your course by adding the first module.
                </p>
                <Button
                  variant="primary"
                  onClick={() => navigate(`/courses/${courseId}/modules/add`)}
                >
                  <Plus size={18} className="mr-1" />
                  Add First Module
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {modules.map((module, index) => (
                  <SortableModule
                    key={module._id}
                    module={module}
                    index={index}
                    courseId={courseId || ""}
                    expandedModules={expandedModules}
                    toggleModuleExpand={toggleModuleExpand}
                    handleDeleteModule={handleDeleteModule}
                    handleDeleteLesson={handleDeleteLesson}
                    navigate={navigate}
                    deletingModuleId={deletingModuleId}
                    deletingLessonId={deletingLessonId}
                    activeDrag={activeDrag}
                    overInfo={overInfo}
                  />
                ))}
              </div>
            )}
          </SortableContext>
        </DndContext>
      </div>
    </DashboardLayout>
  );
}
